import { type FC, useRef } from "react";
import { useIntl } from "react-intl";
import { Text, Card, Group, Title } from "@mantine/core";
import { MapMenu } from "./MapMenu.js";
import type { Style, StyleVersion } from "../map/types/map.js";
import type { Tileset, TilesetDataset, TilesetVersion } from "../tileset/types/tileset.js";
import { formatDistanceToNow } from "date-fns";
import { TilesetMenu } from "./TilesetMenu.js";
import type { AccountInfo } from "@mapka/style-editor";
import { BLACK_IN_LIGHT_MODE } from "@mapka/style-common";
import { dateFnsLocales } from "../../../constants/dateFnsLocales.js";
import { InjectSkeletonLoaders, type ComponentWithLoaderProps } from "../../../components/InjectSkeletonLoaders.js";
import { ScopeBadge } from "../map/components/ScopeBadge.js";

export interface TilesetResource {
  type: "tileset";
  object: Tileset;
  version: TilesetVersion;
  createdAt: string;
  account: AccountInfo;
  dataset: TilesetDataset;
}

export interface MapResource {
  type: "map";
  object: Style;
  version: StyleVersion;
  account: AccountInfo;
  createdAt: string;
}

export type Resource = MapResource | TilesetResource;

const MENU_STYLES = Object.freeze({
  position: "right-start",
  styles: {
    arrow: {
      backgroundColor: BLACK_IN_LIGHT_MODE,
    },
  },
  withArrow: true,
  arrowPosition: "center",
  arrowOffset: 40,
  arrowSize: 10,
  offset: 0,
});

interface QuickAccessCardMenuProps {
  resource: Resource;
}

const QuickAccessCardMenu: FC<QuickAccessCardMenuProps> = ({ resource }) =>
  resource.type === "map" ?
      (
        <MapMenu
          style={resource.object}
          version={resource.version}
          account={resource.account}
          {...MENU_STYLES}
          showShare
        />
      ) :
      (
        <TilesetMenu
          tileset={resource.object}
          version={resource.version}
          dataset={resource.dataset}
          account={resource.account}
          {...MENU_STYLES}
        />
      );

interface QuickAccessCardProps extends ComponentWithLoaderProps {
  resource?: Resource;
  onNavigate: () => void;
};

export function QuickAccessCard({ resource, onNavigate, isLoading }: QuickAccessCardProps) {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const object = resource?.object;

  return (
    <Card
      p={16}
      ref={containerRef}
      onClick={(e) => {
        if (e.currentTarget === containerRef.current) onNavigate();
      }}
    >
      <InjectSkeletonLoaders {...{ isLoading }}>
        <Group gap={4} wrap="nowrap" justify="space-between">
          <Title order={5} lineClamp={1}>
            {object?.displayName}
          </Title>

          {resource && (
            <QuickAccessCardMenu
              resource={resource}
            />
          )}
        </Group>

        <Text mb={48} size="sm" c="gray.6">
          {resource && formatDistanceToNow(new Date(resource.createdAt), {
            addSuffix: true,
            locale: dateFnsLocales[intl.locale],
          })}
        </Text>

        {object && <ScopeBadge scope={object.scope} />}
      </InjectSkeletonLoaders>
    </Card>
  );
};
